export const GMAIL_READONLY_SCOPE =
  "https://www.googleapis.com/auth/gmail.readonly";

const EMAIL_PATTERN = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;

const READ_PATHS = [
  /^\/gmail\/v1\/users\/me\/profile$/,
  /^\/gmail\/v1\/users\/me\/messages$/,
  /^\/gmail\/v1\/users\/me\/messages\/[a-zA-Z0-9_-]+$/,
];

export type LeadEmail = {
  id: number;
  emails: string[];
};

export type GmailHeaders = Record<string, string>;

export type SentMessage = {
  id: string;
  internalDate?: string;
  snippet?: string;
  headers: GmailHeaders;
};

export type PlannedMessage = {
  contactId: number;
  gmailId: string;
  subject: string;
  body: string;
  fromAddress: string;
  toAddress: string;
  sentAt: string;
};

const AUTHORIZE_KEYS = [
  "client_id",
  "redirect_uri",
  "response_type",
  "scope",
  "access_type",
  "prompt",
  "state",
] as const;

/**
 * Account chooser for whichever Gmail she picks. The URL never names a mailbox.
 */
export function buildAuthorizeUrl(input: {
  clientId: string;
  redirectUri: string;
  state: string;
}): string {
  if (!input.clientId || !input.redirectUri || !input.state) {
    throw new Error("Gmail sign-in is not configured.");
  }
  const params = new URLSearchParams({
    client_id: input.clientId,
    redirect_uri: input.redirectUri,
    response_type: "code",
    scope: GMAIL_READONLY_SCOPE,
    access_type: "offline",
    prompt: "select_account consent",
    state: input.state,
  });
  const keys = [...params.keys()];
  if (keys.join(",") !== AUTHORIZE_KEYS.join(",")) {
    throw new Error("Gmail sign-in was built incorrectly.");
  }
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/** Accept only the single read-only Gmail scope. */
export function scopeError(scope: string | undefined): string | null {
  const scopes = (scope ?? "").split(/\s+/).filter(Boolean);
  if (scopes.length === 1 && scopes[0] === GMAIL_READONLY_SCOPE) return null;
  return "Google granted more than read-only access. Vogelkop did not save that sign-in.";
}

export function assertGmailRead(method: string, url: string) {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error("That Gmail request is not allowed.");
  }
  if (method !== "GET" || parsed.origin !== "https://gmail.googleapis.com") {
    throw new Error("That Gmail request is not allowed.");
  }
  if (!READ_PATHS.some((pattern) => pattern.test(parsed.pathname))) {
    throw new Error("That Gmail request is not allowed.");
  }
}

export function sentListUrl(pageToken?: string | null): string {
  const url = new URL(
    "https://gmail.googleapis.com/gmail/v1/users/me/messages",
  );
  url.searchParams.set("q", "in:sent -in:draft -in:chat");
  url.searchParams.set("maxResults", "20");
  if (pageToken) url.searchParams.set("pageToken", pageToken);
  return url.toString();
}

export function messageUrl(id: string): string {
  if (!/^[a-zA-Z0-9_-]+$/.test(id)) {
    throw new Error("Unexpected Gmail message id.");
  }
  const url = new URL(
    `https://gmail.googleapis.com/gmail/v1/users/me/messages/${id}`,
  );
  url.searchParams.set("format", "metadata");
  for (const header of ["From", "To", "Cc", "Bcc", "Subject", "Date"]) {
    url.searchParams.append("metadataHeaders", header);
  }
  return url.toString();
}

export const PROFILE_URL =
  "https://gmail.googleapis.com/gmail/v1/users/me/profile";

export function emailsInHeader(value: string | null | undefined): string[] {
  if (!value) return [];
  return [...value.matchAll(EMAIL_PATTERN)].map((match) =>
    match[0].toLowerCase().replace(/[.,;:]+$/, ""),
  );
}

export function headersByName(
  headers: { name: string; value: string }[] | undefined,
): GmailHeaders {
  const out: GmailHeaders = {};
  for (const header of headers ?? []) {
    out[header.name.toLowerCase()] = header.value;
  }
  return out;
}

export function callbackQuery(search: string, hash: string) {
  const hashQuery = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
  const params = new URLSearchParams(search.replace(/^\?/, "") || hashQuery);
  return {
    code: params.get("code"),
    state: params.get("state"),
    error: params.get("error"),
  };
}

export function mailboxAddress(email: string | undefined): string {
  const matches = emailsInHeader(email);
  if (matches.length !== 1) {
    throw new Error("Google did not identify the mailbox.");
  }
  return matches[0];
}

export function decodeSnippet(value: string | undefined): string {
  if (!value) return "";
  return value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, digits: string) =>
      String.fromCodePoint(Number(digits)),
    )
    .replace(/&#x([0-9a-f]+);/gi, (_, digits: string) =>
      String.fromCodePoint(parseInt(digits, 16)),
    )
    .trim();
}

export function indexLeads(leads: LeadEmail[]): Map<string, number[]> {
  const map = new Map<string, number[]>();
  for (const lead of leads) {
    for (const email of lead.emails) {
      const key = email.trim().toLowerCase();
      if (!key) continue;
      const list = map.get(key) ?? [];
      if (!list.includes(lead.id)) list.push(lead.id);
      map.set(key, list);
    }
  }
  return map;
}

function sentAtFrom(message: SentMessage, now: string): string {
  if (message.internalDate && /^\d+$/.test(message.internalDate)) {
    const date = new Date(Number(message.internalDate));
    if (!Number.isNaN(date.valueOf())) return date.toISOString();
  }
  const header = message.headers.date;
  if (header) {
    const date = new Date(header);
    if (!Number.isNaN(date.valueOf())) return date.toISOString();
  }
  return now;
}

/**
 * Attach a sent message to every lead whose email is a recipient.
 * The connected mailbox is skipped. Any mailbox is allowed.
 */
export function planSentImports(input: {
  mailbox: string;
  messages: SentMessage[];
  leads: LeadEmail[];
  already: { contactId: number; gmailId: string }[];
  now?: string;
}): PlannedMessage[] {
  const mailbox = mailboxAddress(input.mailbox);
  const byEmail = indexLeads(input.leads);
  const seen = new Set(
    input.already.map((row) => `${row.contactId}:${row.gmailId}`),
  );
  const now = input.now ?? new Date().toISOString();
  const planned: PlannedMessage[] = [];

  for (const message of input.messages) {
    const recipients = [
      ...new Set([
        ...emailsInHeader(message.headers.to),
        ...emailsInHeader(message.headers.cc),
        ...emailsInHeader(message.headers.bcc),
      ]),
    ].filter((email) => email !== mailbox);
    const sentAt = sentAtFrom(message, now);
    const subject = message.headers.subject?.trim() || "Sent email";
    const body =
      decodeSnippet(message.snippet).slice(0, 4000) || "(no message text)";
    const fromAddress = emailsInHeader(message.headers.from)[0] || mailbox;

    for (const recipient of recipients) {
      for (const contactId of byEmail.get(recipient) ?? []) {
        const key = `${contactId}:${message.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        planned.push({
          contactId,
          gmailId: message.id,
          subject,
          body,
          fromAddress,
          toAddress: recipient,
          sentAt,
        });
      }
    }
  }

  return planned;
}
